// Measurement utilities for converting between Imperial and Metric systems

export const measurementUnits = {
  metric: {
    distance: 'km',
    distanceLong: 'kilometers',
    volume: 'L',
    volumeLong: 'liters',
    temperature: '°C',
    temperatureLong: 'celsius'
  },
  imperial: {
    distance: 'mi',
    distanceLong: 'miles',
    volume: 'gal',
    volumeLong: 'gallons', 
    temperature: '°F',
    temperatureLong: 'fahrenheit'
  }
};

// Convert kilometers to miles and vice versa
export const convertDistance = (value, fromSystem, toSystem) => {
  if (!value || fromSystem === toSystem) return value;
  
  if (fromSystem === 'metric' && toSystem === 'imperial') {
    // km to miles
    return Math.round(value * 0.621371);
  } else if (fromSystem === 'imperial' && toSystem === 'metric') {
    // miles to km
    return Math.round(value * 1.60934);
  }
  
  return value;
};

// Convert liters to gallons and vice versa
export const convertVolume = (value, fromSystem, toSystem) => {
  if (!value || fromSystem === toSystem) return value;
  
  if (fromSystem === 'metric' && toSystem === 'imperial') {
    // liters to gallons
    return Math.round(value * 0.264172 * 100) / 100;
  } else if (fromSystem === 'imperial' && toSystem === 'metric') {
    // gallons to liters
    return Math.round(value * 3.78541 * 100) / 100;
  }
  
  return value;
};

// Get formatted distance with appropriate unit
export const formatDistance = (value, measurementSystem = 'metric') => {
  if (!value && value !== 0) return '';
  
  const unit = measurementUnits[measurementSystem]?.distance || 'km';
  return `${value.toLocaleString()} ${unit}`;
};

// Get formatted volume with appropriate unit  
export const formatVolume = (value, measurementSystem = 'metric') => {
  if (!value && value !== 0) return '';
  
  const unit = measurementUnits[measurementSystem]?.volume || 'L';
  return `${value.toLocaleString()} ${unit}`;
};

// Get distance unit label
export const getDistanceUnit = (measurementSystem = 'metric') => {
  return measurementUnits[measurementSystem]?.distance || 'km';
};

// Get distance unit long label
export const getDistanceUnitLong = (measurementSystem = 'metric') => {
  return measurementUnits[measurementSystem]?.distanceLong || 'kilometers';
};

// Get volume unit label
export const getVolumeUnit = (measurementSystem = 'metric') => {
  return measurementUnits[measurementSystem]?.volume || 'L';
};

// Get volume unit long label
export const getVolumeUnitLong = (measurementSystem = 'metric') => {
  return measurementUnits[measurementSystem]?.volumeLong || 'liters';
};

// Example usage functions
export const getMeasurementExample = (measurementSystem = 'metric') => {
  if (measurementSystem === 'metric') {
    return 'Example: 100,000 km, 50 L fuel';
  } else {
    return 'Example: 62,137 mi, 13.2 gal fuel';
  }
};

export const getMeasurementDescription = (measurementSystem = 'metric') => {
  if (measurementSystem === 'metric') {
    return 'Kilometers, liters, celsius (km, L, °C)';
  } else {
    return 'Miles, gallons, fahrenheit (mi, gal, °F)';
  }
};

// Fuel-specific utilities
export const getFuelUnit = (measurementSystem = 'metric') => {
  return measurementUnits[measurementSystem]?.volume || 'L';
};

export const getFuelUnitLong = (measurementSystem = 'metric') => {
  return measurementUnits[measurementSystem]?.volumeLong || 'liters';
};

export const getEfficiencyUnit = (measurementSystem = 'metric') => {
  return measurementSystem === 'metric' ? 'L/100km' : 'MPG';
};

export const getEfficiencyUnitLong = (measurementSystem = 'metric') => {
  return measurementSystem === 'metric' ? 'Liters per 100 kilometers' : 'Miles per gallon';
};

export const formatFuelAmount = (amount, measurementSystem = 'metric') => {
  if (!amount && amount !== 0) return '';
  
  const unit = getFuelUnit(measurementSystem);
  return `${amount.toLocaleString()} ${unit}`;
};

export const formatEfficiency = (efficiency, measurementSystem = 'metric') => {
  if (!efficiency && efficiency !== 0) return '';
  
  const unit = getEfficiencyUnit(measurementSystem);
  return `${efficiency} ${unit}`;
};

export const formatCurrency = (amount, currency = 'USD') => {
  if (!amount && amount !== 0) return '';
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(amount);
};